import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { requestRightPanelClose } from '@/lib/panels/right-panel-events'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'
import {
  clampTextBoxDimensions,
  meetsTextBoxMinDimensions,
  meetsTextDragThreshold,
} from '@/lib/text/text-tool-create'
import { computeDragSpanRect } from '../helpers/drag-rect'
import { buildTextFrameInitialParagraphs } from '../helpers/text-frame-initial-paragraphs'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { TextDragCreateState } from '../types/canvas-drag-state'

/**
 * Type-tool pasteboard gestures: a click on a shape starts editing its text, a click on empty
 * canvas returns to Select, and a drag creates a new text frame and starts editing it.
 */
export function useTextDragCreate({
  internalShapeManagerRef,
  isInTextCreationMode,
  scaleRef,
  getCanvasPointFromPointerEvent,
  isPointerOnEditingShapeText,
  tryEnterShapeTextEditFromPointer,
  clearShapeSelectionWithHistory,
  setSelectedGuideIds,
  onBackgroundClick,
  onActivateSelectTool,
  newShapeTextSeedStyle,
  newTextBoxKerning,
  newTextBoxTracking,
  newTextBoxUnderline,
  newTextBoxAlignment,
  defaultFontSize,
  resolvedDefaultFontFamilyId,
  resolvedDefaultFontId,
  spreadId,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isInTextCreationMode: boolean
  scaleRef: MutableRefObject<number>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  isPointerOnEditingShapeText: (clientX: number, clientY: number) => boolean
  tryEnterShapeTextEditFromPointer: (clientX: number, clientY: number) => boolean
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
  onBackgroundClick: CenterCanvasConfig['onBackgroundClick']
  onActivateSelectTool: CenterCanvasConfig['onActivateSelectTool']
  newShapeTextSeedStyle: NewShapeTextSeedStyle
  newTextBoxKerning: CenterCanvasConfig['newTextBoxKerning']
  newTextBoxTracking: CenterCanvasConfig['newTextBoxTracking']
  newTextBoxUnderline: CenterCanvasConfig['newTextBoxUnderline']
  newTextBoxAlignment: CenterCanvasConfig['newTextBoxAlignment']
  defaultFontSize: CenterCanvasConfig['defaultFontSize']
  resolvedDefaultFontFamilyId: number | undefined
  resolvedDefaultFontId: number | undefined
  spreadId: CenterCanvasConfig['spreadId']
}) {
  // Text creation drag state
  const textDragCreateRef = useRef<TextDragCreateState | null>(null)
  const [textDragCreate, setTextDragCreate] = useState<typeof textDragCreateRef.current>(null)
  const textDragRafRef = useRef<number | null>(null)

  // Text creation drag start
  const handleTextDragCreatePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (!isInTextCreationMode) return false

      if (e.button !== 0) return true

      const mgr = internalShapeManagerRef.current
      const editingShapeTextId = mgr?.getEditingShapeTextId()

      if (editingShapeTextId) {
        if (isPointerOnEditingShapeText(e.clientX, e.clientY)) {
          return true
        }

        // Clicking outside the text being edited always ends that session — but whether it
        // ALSO leaves the Text tool depends on what THIS gesture turns out to be, so that
        // part is deferred to pointer-up (WMPCM-10547) instead of decided here. Falling
        // through (rather than returning) lets the exact same click/drag split below run: a
        // plain click still lands on the "nothing hit, back to Select" branch there (same as
        // the click-outside-with-no-active-edit case), while a click-and-drag instead arms a
        // new `textDragCreateRef` for the NEXT frame, so the Text tool can draw several
        // frames back to back without being reselected between them. Forcing
        // `activateSelectTool` and returning here — as this used to — discarded the drag
        // outright: `textDragCreateRef` never got armed, so the pointer-up below never had a
        // gesture to inspect.
        mgr?.exitShapeTextEdit()
        clearShapeSelectionWithHistory([])
      }

      // Clicking an existing shape (not already mid-edit above) starts editing its text in
      // place instead of drag-creating a new box on top of it — shapes are pointer-events:none
      // while the Type tool is active (see isInShapeCreationMode/isInTextCreationMode usage in
      // ShapeRenderer), so this manual hit-test is the only way to detect the click.
      if (tryEnterShapeTextEditFromPointer(e.clientX, e.clientY)) {
        e.preventDefault()
        e.stopPropagation()
        return true
      }

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return true
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      textDragCreateRef.current = {
        pointerId: e.pointerId,
        startCanvasX: pt.canvasX,
        startCanvasY: pt.canvasY,
        currentCanvasX: pt.canvasX,
        currentCanvasY: pt.canvasY,
      }
      return true
    },
    [
      getCanvasPointFromPointerEvent,
      isInTextCreationMode,
      isPointerOnEditingShapeText,
      tryEnterShapeTextEditFromPointer,
      clearShapeSelectionWithHistory,
      internalShapeManagerRef,
    ]
  )

  // Text creation drag tracking
  const handleTextDragCreatePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      const textDrag = textDragCreateRef.current

      if (!textDrag || e.pointerId !== textDrag.pointerId) return false

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return true
      e.preventDefault()
      textDragCreateRef.current = {
        ...textDrag,
        currentCanvasX: pt.canvasX,
        currentCanvasY: pt.canvasY,
      }
      if (textDragRafRef.current == null) {
        textDragRafRef.current = requestAnimationFrame(() => {
          textDragRafRef.current = null
          setTextDragCreate(textDragCreateRef.current)
        })
      }

      return true
    },
    [getCanvasPointFromPointerEvent]
  )

  // Text creation drag release — create the text frame. It is a real, persisted element
  // from this point on: nothing discards it later for still being empty (WMPCM-9763).
  const handleTextDragCreatePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      const textDrag = textDragCreateRef.current

      if (!textDrag || e.pointerId !== textDrag.pointerId) return false

      textDragCreateRef.current = null
      setTextDragCreate(null)
      if (textDragRafRef.current != null) {
        cancelAnimationFrame(textDragRafRef.current)
        textDragRafRef.current = null
      }

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      const dx = textDrag.currentCanvasX - textDrag.startCanvasX
      const dy = textDrag.currentCanvasY - textDrag.startCanvasY
      const canvasScale = scaleRef.current
      const isDrag = meetsTextDragThreshold(dx, dy, canvasScale)

      const mgr = internalShapeManagerRef.current

      if (!mgr) return true

      if (!isDrag) {
        // Text-unification Phase 2: a newly created text box is now a ShapeManager-owned
        // shape, so its edit state lives in `mgr.getEditingShapeTextId()`.
        const editingShapeTextId = mgr.getEditingShapeTextId()

        if (editingShapeTextId) {
          // A plain click on empty canvas while editing exits the session AND deselects the
          // frame, same as the "nothing to edit" branch below — clicking the pasteboard is a
          // deliberate back-out, not a reason to leave the frame selected underneath
          // (exitShapeTextEdit itself doesn't touch selection, so this has to do it explicitly).
          mgr.exitShapeTextEdit({ activateSelectTool: true })
          clearShapeSelectionWithHistory([])
          setSelectedGuideIds(new Set())
          onBackgroundClick?.()
          // WMPCM-11032: an empty-space click collapses every panel, not just the left one.
          requestRightPanelClose()
          return true
        }

        clearShapeSelectionWithHistory([])
        setSelectedGuideIds(new Set())
        onBackgroundClick?.()
        // WMPCM-11032: an empty-space click collapses every panel, not just the left one.
        requestRightPanelClose()
        // A plain click on empty canvas (or a non-text shape) hit neither an editing session
        // above nor tryEnterShapeTextEditFromPointer in the pointer-down handler — nothing here
        // is text to edit, so return to Select the same way Escape already does in this state.
        onActivateSelectTool?.()
        return true
      }

      if (mgr.getEditingShapeTextId()) {
        mgr.exitShapeTextEdit()
      }

      const { x: boxX, y: boxY, width: boxW, height: boxH } = computeDragSpanRect(textDrag)

      if (!meetsTextBoxMinDimensions(boxW, boxH, canvasScale)) {
        if (mgr.getEditingShapeTextId()) {
          mgr.exitShapeTextEdit()
        }

        clearShapeSelectionWithHistory([])
        setSelectedGuideIds(new Set())
        return true
      }

      const dims = clampTextBoxDimensions(boxW, boxH, canvasScale)

      const initialParagraphsForDrag: Paragraph[] = buildTextFrameInitialParagraphs({
        newTextBoxAlignment,
        defaultFontSize,
        resolvedDefaultFontId,
        resolvedDefaultFontFamilyId,
        newTextBoxKerning,
        newTextBoxTracking,
        newTextBoxUnderline,
      })

      const newTextBox: TextBoxData = {
        id: crypto.randomUUID(),
        x: boxX,
        y: boxY,
        width: dims.width,
        height: dims.height,
        type: 'square',
        kindId: 1,
        // Explicit, not merely absent: `ShapeRenderer` defaults an unset fill/stroke to a
        // visible gray/black (so a freshly-drawn shape is visible before the user picks a
        // color), which is wrong for a text frame — see the matching comment in
        // checkpoint-to-text.ts (text-unification Phase 2).
        fill: 'transparent',
        stroke: 'transparent',
        strokeWidth: 0,
        paragraphs: initialParagraphsForDrag,
        opacity: 1,
        spreadId,
      }

      clearShapeSelectionWithHistory([])
      // Text-unification Phase 2: a new text box is created directly as a ShapeManager shape
      // — `addShapeData` fires `onShapeCreated` (home.tsx `handleShapeCreated`), the same
      // fractional-index + undo `add` action + `enqueueAdd(buildStowedElement(...))` path a
      // hand-drawn shape gets, since a `paragraphs`-bearing rectangle IS a shape now.
      // `selectAfterCreate: false` because `enterShapeTextEdit` selects it itself, below.
      mgr.addShapeData(newTextBox, { selectAfterCreate: false })
      // A brand-new frame has no content to click "into" — the caret must start at the very
      // beginning (top-left), never derived from the drag's release point (which can be the
      // opposite corner from where the frame visually starts, e.g. bottom-right after a
      // top-left-to-bottom-right drag).
      mgr.enterShapeTextEdit(newTextBox.id, {
        caretOffset: 0,
        seedStyle: newShapeTextSeedStyle,
      })
      return true
    },
    [
      newShapeTextSeedStyle,
      onBackgroundClick,
      onActivateSelectTool,
      clearShapeSelectionWithHistory,
      newTextBoxKerning,
      newTextBoxTracking,
      newTextBoxUnderline,
      newTextBoxAlignment,
      defaultFontSize,
      resolvedDefaultFontFamilyId,
      resolvedDefaultFontId,
      spreadId,
      internalShapeManagerRef,
      scaleRef,
      setSelectedGuideIds,
    ]
  )

  return {
    textDragCreateRef,
    textDragCreate,
    setTextDragCreate,
    textDragRafRef,
    handleTextDragCreatePointerDown,
    handleTextDragCreatePointerMove,
    handleTextDragCreatePointerUp,
  }
}
